import { Modal } from "newui/components/ui/Modal";
import { Button } from "newui/components/Button";
import type { UserRoleAssignment } from "newui/features/RolesPermissions/types/RolesPermissions.type";
import { AccessLevelBadge, ManageRolesItemCell } from "./ManageRolesTable";

interface DeleteConfirmModalProps {
	isOpen: boolean;
	/** EXACTLY the checked rows — this list is the promise the confirm step makes. */
	assignments: UserRoleAssignment[];
	isDeleting: boolean;
	/**
	 * Server-supplied `failures[].message` values from the atomic-failure response
	 * (CONTRACT §6). Empty array = no error. Rendered inline and persistently, not
	 * as a toast: the admin needs it still on screen while deciding which row to
	 * deselect.
	 */
	errorMessages: string[];
	onCancel: () => void;
	onConfirm: () => void;
}

/**
 * PRO-757 (D7-T2) — the second layer of the Manage Roles modal: name every row
 * that is about to be deleted, then delete exactly those.
 *
 * Presentational by design. The mutation lives in `ManageRolesModal`, which owns
 * the selection this dialog describes — so there is no way for the list shown
 * here and the ids sent to the server to drift apart.
 */
export const DeleteConfirmModal = ({
	isOpen,
	assignments,
	isDeleting,
	errorMessages,
	onCancel,
	onConfirm,
}: DeleteConfirmModalProps) => {
	const hasError = errorMessages.length > 0;

	const footer = (
		<div className="flex w-full justify-end gap-2">
			<Button
				btntype="plain-border"
				size="sm"
				disabled={isDeleting}
				onClick={onCancel}
				data-testid="manage-roles-delete-cancel"
			>
				Cancel
			</Button>
			<Button
				btntype="danger"
				size="sm"
				disabled={isDeleting || assignments.length === 0}
				isLoading={isDeleting}
				onClick={onConfirm}
				data-testid="manage-roles-delete-confirm"
			>
				Confirm delete
			</Button>
		</div>
	);

	return (
		<Modal
			isOpen={isOpen}
			onClose={onCancel}
			title="Delete roles"
			iconType="warning"
			titleColor="red.600"
			size="lg"
			// F8 — Escape is this dialog's Cancel, including while the revoke is in
			// flight: Cancel is disabled then (the request is already gone), so
			// Escape is too, rather than hiding the outcome the admin is waiting on.
			closeOnEsc={!isDeleting}
			customFooter={footer}
		>
			<div data-testid="manage-roles-delete-confirm-modal">
				{hasError && (
					<div
						role="alert"
						className="mb-3 rounded-md border border-red-200 bg-red-50 px-3 py-2 text-sm text-red-700"
						data-testid="manage-roles-delete-error"
					>
						<p className="font-semibold">Nothing was deleted.</p>
						<ul className="mt-1 list-disc pl-4">
							{errorMessages.map((message) => (
								<li key={message}>{message}</li>
							))}
						</ul>
					</div>
				)}
				<p className="mb-3 text-sm text-gray-600">
					{assignments.length === 1
						? "This role assignment will be removed from this user."
						: `These ${assignments.length} role assignments will be removed from this user.`}
				</p>
				<ul
					className="flex flex-col gap-1.5"
					data-testid="manage-roles-delete-confirm-list"
				>
					{assignments.map((assignment) => (
						<li
							key={assignment.assignment_id}
							className="flex flex-wrap items-center gap-2 rounded-md border border-gray-200 px-3 py-2 text-sm"
							data-testid={`manage-roles-delete-confirm-row-${assignment.assignment_id}`}
						>
							<span className="font-medium text-gray-800">{assignment.role_name}</span>
							<AccessLevelBadge
								scopeType={assignment.scope_type}
								testId={`delete-confirm-badge-${assignment.assignment_id}`}
							/>
							<span className="text-gray-700">
								<ManageRolesItemCell assignment={assignment} />
							</span>
						</li>
					))}
				</ul>
			</div>
		</Modal>
	);
};
